import React, { useEffect, useRef, useState } from "react";
import { GLOBAL_CONST } from "../../../constants";
import { EventLogger } from "../../../common/utils/EventLogger";
import { delay, redirect, redirectLoaded, sendAnalytics } from "../../../utils";
import LoadingState from "./states/LoadingState";
import ErrorState from "./states/ErrorState";
import SuccessState from "./states/SuccessState";
import QuotaState from "./states/QuotaState";
import WarningState from "./states/WarningState";
import { BarcodeWrapper } from "./styles";
import useBarcode from "../../hooks/useBarcode";
import axios from "axios";

interface BarcodeComponentProps {
  barcodeConfig: {
    loading: {
      text: string;
    };
    error: {
      topText: string;
      title: string;
      subtitle: string;
      bottomText: string;
      icon: string;
    };
    quota: {
      text: string;
      icon: string;
    };
    warning?: {
      icon: string;
      messages?: Record<string, string>;
    };
    success: {
      topText: string;
      bottomText: string;
      hintCard: {
        title: string;
        image: string;
        content: string;
        buttonText: string;
        icon: string;
      };
    };
  };
  isActive: boolean | null;
  driverLevel?: string;
  legalText?: string;
}

const BarcodeComponent: React.FC<BarcodeComponentProps> = ({
  barcodeConfig,
  isActive,
  driverLevel,
  legalText,
}) => {
  const { status, barcode, warning, lastError, fetchBarcode } = useBarcode(
    isActive,
    driverLevel
  );
  const [brightnessCalled, setBrightnessCalled] = useState(false);
  const previousStatusRef = useRef<string | null>(null);

  const getDynamicText = (text: string) => {
    if (!barcode) {
      return text;
    }
    const quotaLimit = barcode.quotaLimit ?? 0;
    const quotaRemaining = barcode.quotaRemaining ?? 0;
    const quotaUsed = quotaLimit - quotaRemaining;
    return text
      .replace("{quotaUsed}", quotaUsed.toString())
      .replace("{quotaLimit}", quotaLimit.toString());
  };

  // Set screen brightness and signal page load when barcode data is successfully fetched.
  useEffect(() => {
    if (status === "success" && barcode?.barcodeNumber && isActive && !brightnessCalled) {
      const setBrightnessAndFinalize = async () => {
        try {
          setBrightnessCalled(true);

          // Notify the native host that onboarding is complete.
          await redirect(`${GLOBAL_CONST.CLIENT_ID}://rewards/onboarding-complete`);
          await delay(100);

          // Request max brightness from the native host.
          await redirect(`${GLOBAL_CONST.CLIENT_ID}://rewards/max-brightness`);
          await delay(100);

          // Notify the native host that the webview is loaded and ready.
          redirectLoaded();
          await delay(100);

          // Log analytics for the successful display of the barcode.
          await sendAnalytics("barcodeSuccessState");
          await delay(200);

        } catch (error) {
          console.warn("Error during brightness/load sequence:", error);
          // If the sequence fails, still attempt to signal that the page has loaded.
          redirectLoaded();
        }
      };

      setBrightnessAndFinalize();
    }
  }, [status, barcode, isActive, brightnessCalled]);

  // Send analytics when barcode status changes to error or quotaUsed.
  useEffect(() => {
    if (!isActive) return;
    if (previousStatusRef.current === status) return;
    previousStatusRef.current = status;

    const sendStatusAnalytics = async () => {
      if (status === "error" || status === "quotaUsed") {
        await delay(100);
        await sendAnalytics("barcodeFailState", {
          errorMsg: lastError?.errorMsg || status,
          debugInfo: lastError?.debugInfo || status,
        });
        await delay(200);
      } else if (status === "warning") {
        await delay(100);
        await sendAnalytics("barcodeWarningState", {
          errorMsg: warning?.code || status,
          debugInfo: `getBarcode+200+${warning?.code ?? "warning"}`,
        });
        await delay(200);
      }
    };

    sendStatusAnalytics().catch((err) =>
      console.warn("Analytics failed for barcode status:", err)
    );
  }, [status, isActive, lastError, warning]);

  const handleRefresh = async () => {
    if (status === "loading") {
      return;
    }

    try {
      await fetchBarcode(true);
    } catch (error) {
      console.error("Error during refresh:", error);

      try {
        await EventLogger.logErrorEvent("Error during barcode refresh", error);
      } catch (logError) {
        console.error("Failed to log barcode refresh error:", logError);
      }

      const is403Error = axios.isAxiosError(error) &&
                        error.response &&
                        error.response.status === 403;

      if (!is403Error) {
        await fetchBarcode(true);
      }
    }
  };

  const renderState = () => {
    if (!isActive) {
      return <LoadingState />;
    }

    switch (status) {
      case "loading":
        return <LoadingState />;
      case "error":
        return (
          <ErrorState
            onRefresh={handleRefresh}
            title={barcodeConfig.error.title}
            subtitle={barcodeConfig.error.subtitle}
            icon={barcodeConfig.error.icon}
          />
        );
      case "quotaUsed":
        return (
          <QuotaState
            text={getDynamicText(barcodeConfig.quota.text)}
            icon={barcodeConfig.quota.icon}
          />
        );
      case "warning": {
        const warningText =
          (warning?.code && barcodeConfig.warning?.messages?.[warning.code]) ||
          warning?.message ||
          "This offer is not currently available.";
        return (
          <WarningState
            text={warningText}
            icon={barcodeConfig.warning?.icon ?? barcodeConfig.quota.icon}
          />
        );
      }
      case "success": {
        const barcodeNumber = barcode?.barcodeNumber;
        if (!barcodeNumber) {
          console.warn("Success state but no barcode number available", {
            status,
            barcode,
            quotaRemaining: barcode?.quotaRemaining
          });
          return (
            <ErrorState
              onRefresh={handleRefresh}
              title={barcodeConfig.error.title}
              subtitle={barcodeConfig.error.subtitle}
              icon={barcodeConfig.error.icon}
            />
          );
        }
        return (
          <SuccessState
            barcode={barcodeNumber}
            topText={getDynamicText(barcodeConfig.success.topText)}
            bottomText={getDynamicText(barcodeConfig.success.bottomText)}
            legal={legalText}
            hintCard={barcodeConfig.success.hintCard}
          />
        );
      }
      default:
        return <LoadingState />;
    }
  };

  return <BarcodeWrapper>{renderState()}</BarcodeWrapper>;
};

export default BarcodeComponent;
